/**
 * components/fiscal/VendorFiscalCoveragePanel.js
 * ----------------------------------------------------------------------------
 * The vendor's read-only view of how much of their catalogue carries URA
 * product codes, from GET /api/fiscal-classification/my-coverage.
 *
 * WHY READ-ONLY, SAID ON THE PANEL. Every line on an EFRIS invoice or receipt
 * needs a URA commodity code and a unit of measure. A vendor knows their
 * supply; they do not know URA's classification of it, and a guessed code
 * misdeclares a real supply. So Lusuku staff set the codes (the URA
 * classification page under Tax) and the vendor sees the result here, with
 * one sentence saying who to ask when something looks wrong. There are no
 * edit controls on purpose; the backend would refuse them anyway.
 *
 * WHY IT IS HERE AT ALL. A vendor whose receipts stop because a product has no
 * code should be able to see that from their own profile instead of being
 * told by support after the fact. The panel is mounted once, inside
 * components/VendorTaxSettings.js, which every vendor type already shows on
 * its Tax and receipts tab, so there is one call site rather than four.
 *
 * It fails quietly. A 403 or 404 (the account has no catalogue, or the
 * endpoint has not shipped yet) renders nothing; any other error renders one
 * plain sentence. A panel about tax readiness must never be the thing that
 * breaks the tax settings screen.
 */
import React, { useEffect, useState } from "react";
import { apiMyFiscalClassificationCoverage } from "../../api/index.js";
import { TYPE } from "./fiscalUi.js";
import TaxHelp from "./taxHelp.js";

const S = {
  wrap: { borderTop: "1px solid #e2e8f0", paddingTop: 16 },
  title: { ...TYPE.body, fontWeight: 700, color: "#0f2237", marginBottom: 3 },
  help: { ...TYPE.secondary, color: "#8595a8", lineHeight: 1.5, maxWidth: 560, marginBottom: 12 },
  row: { ...TYPE.secondary, display: "grid", gridTemplateColumns: "minmax(120px, 180px) 1fr auto", gap: "4px 14px", alignItems: "center", color: "#51637a", padding: "6px 0" },
  track: { height: 6, borderRadius: 999, background: "#eef2f7", overflow: "hidden" },
  fill: (pct) => ({ width: `${pct}%`, height: "100%", background: pct === 100 ? "#00875a" : "#083860", transition: "width 0.2s" }),
  count: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: "#0f2237", fontWeight: 600 },
  total: { ...TYPE.secondary, color: "#51637a", marginTop: 8 },
  note: { ...TYPE.secondary, color: "#8595a8", lineHeight: 1.55 },
};

const percent = (classified, total) => (total ? Math.round((classified / total) * 100) : 0);

const VendorFiscalCoveragePanel = () => {
  const [coverage, setCoverage] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | hidden | error

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiMyFiscalClassificationCoverage();
        if (cancelled) return;
        setCoverage(data || null);
        setState("ready");
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        setState(status === 403 || status === 404 ? "hidden" : "error");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === "hidden") return null;

  const catalogues = (coverage?.catalogues || []).filter((c) => Number(c.total) > 0);
  const totals = coverage?.totals || { total: 0, classified: 0, unclassified: 0 };

  return (
    <div style={S.wrap} data-testid="vendor-fiscal-coverage">
      <div style={S.title}>URA product codes on your catalogue<TaxHelp topic="vendorCoverage" /></div>
      <div style={S.help}>
        Every receipt line needs a URA commodity code and a unit of measure. Lusuku staff set these URA codes on
        your products; you cannot change them here. If one looks wrong, contact Lusuku support and ask for it to
        be changed.
      </div>

      {state === "loading" ? <div style={S.note}>Checking your product codes...</div> : null}
      {state === "error" ? <div style={S.note}>Your product code coverage could not be loaded right now.</div> : null}

      {state === "ready" ? (
        catalogues.length === 0 ? (
          <div style={S.note}>No products to classify yet. Codes appear here as your catalogue is mapped.</div>
        ) : (
          <>
            {catalogues.map((c) => {
              const pct = percent(Number(c.classified) || 0, Number(c.total) || 0);
              return (
                <div key={c.key} style={S.row}>
                  <span>{c.label || c.key}</span>
                  <div style={S.track} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${c.label || c.key} mapped`}>
                    <div style={S.fill(pct)} />
                  </div>
                  <span style={S.count}>{c.classified} of {c.total} mapped</span>
                </div>
              );
            })}
            <div style={S.total}>
              {totals.unclassified > 0
                ? `${totals.unclassified} product${totals.unclassified === 1 ? "" : "s"} still waiting for a URA code. Receipts for those lines cannot be filed until Lusuku maps them.`
                : "Every product has its URA code and unit of measure."}
            </div>
          </>
        )
      ) : null}
    </div>
  );
};

export default VendorFiscalCoveragePanel;
